// src/i18n/i18nConfig.ts
//
// Single source of truth for i18n behavior. Imported by:
//   - src/i18n/useT.ts            (runtime hook)
//   - scripts/extract-strings.mjs (build-time extractor)
//   - scripts/codemod-i18n.mjs    (one-shot wrapper)
//   - scripts/eslint-rules/no-bare-strings.cjs
//
// One file, three consumers, identical rules. Change behavior here.

/* ───────────────────────── Hashing ───────────────────────── */

/** Stable, deterministic short hash of a source string. Used as
 *  the dictionary key in strings.en.json and translations/{lang}
 *  in Firestore. FNV-1a 32-bit, returned as 8-char lowercase hex.
 *
 *  Why FNV-1a (not SHA-1):
 *   - Synchronous in the browser (no SubtleCrypto async).
 *   - Trivially identical in Node + browser, no shim.
 *   - 8 hex chars = 4.3 billion buckets → collision negligible at
 *     our scale (low-thousands of UI strings).
 *
 *  Why a hash (not the source as key):
 *   - Source strings can be long, contain quotes / newlines, and
 *     in some Firestore client paths look weird as map keys. A
 *     fixed-length lowercase-hex key is clean across all storage
 *     and comparison contexts.
 *
 *  Source is NORMALIZED before hashing (whitespace collapse) so
 *  cosmetic edits to spacing in JSX don't invalidate translations.
 *  Capitalization, punctuation, and word choice DO change the
 *  hash — those are real edits. */
export function hashSource(source: string): string {
  const s = normalizeSource(source);
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = (h + ((h << 1) + (h << 4) + (h << 7) + (h << 8) + (h << 24))) >>> 0;
  }
  return ('00000000' + h.toString(16)).slice(-8);
}

/** Trim and collapse internal whitespace. Keeps the first
 *  meaningful word vs a leading newline visually identical for
 *  hashing purposes — JSX often has incidental whitespace from
 *  formatting that we don't want to count as a string change. */
export function normalizeSource(source: string): string {
  return source.replace(/\s+/g, ' ').trim();
}

/* ───────────────────────── Extraction rules ───────────────────────── */

/** File globs (relative to project root) that the extractor and
 *  codemod walk. */
export const SOURCE_GLOBS = ['src/**/*.tsx', 'src/**/*.ts'];

/** Files / directories the extractor and codemod SKIP entirely.
 *  Legal copy is excluded by spec — privacy and terms must render
 *  in their original lawyer-vetted English. */
export const EXCLUDED_PATHS = [
  // Legal pages — original wording is required.
  'src/pages/Privacy.tsx',
  'src/pages/Terms.tsx',
  // The i18n module itself — no point translating its internals.
  'src/i18n/',
  // Type defs, generated files, tests.
  'src/vite-env.d.ts',
  'src/**/*.test.ts',
  'src/**/*.test.tsx',
  'src/**/__mocks__/',
];

/** JSX prop names whose string values represent visible UI text
 *  and should be translated. The codemod wraps these; the ESLint
 *  rule flags un-wrapped ones. */
export const TRANSLATABLE_PROPS = new Set([
  // Text content
  'placeholder',
  'title',
  'label',
  'helperText',
  'errorText',
  'description',
  'subheader',
  // Accessibility — translated for screen readers
  'alt',
  'aria-label',
  'aria-description',
  'aria-placeholder',
  'aria-roledescription',
  'aria-valuetext',
  // MUI-specific
  'inputLabel',
  'noOptionsText',
  'loadingText',
]);

/** A bare string is "translatable" if it has at least one letter
 *  and meets minimum length. Filters out punctuation-only,
 *  whitespace-only, single-char, and code-like fragments. */
export function isTranslatable(rawText: string): boolean {
  const text = normalizeSource(rawText);
  if (text.length < 2) return false;
  // Must contain at least one Unicode letter (any script).
  if (!/\p{L}/u.test(text)) return false;
  // Skip URLs and email-ish patterns.
  if (/^https?:\/\//.test(text)) return false;
  if (/^[\w.+-]+@[\w-]+\.[\w.-]+$/.test(text)) return false;
  // Skip CSS values and class names that sometimes appear as text.
  if (/^[\w-]+:\s*[\w-]+(;\s*[\w-]+:\s*[\w-]+)*$/.test(text)) return false;
  // Skip strings that look like file paths / module ids.
  if (/^[./][\w/.-]+$/.test(text)) return false;
  // Skip pure numerics with units ("100ms", "50%", "1.5rem").
  if (/^-?\d+(\.\d+)?(ms|s|px|rem|em|%|vh|vw|deg)?$/.test(text)) return false;
  return true;
}

/** Source string the runtime treats as a no-op marker. Used by
 *  the codemod when it spots a translatable string but can't find
 *  a place to wrap it; emits a warning instead of a broken edit. */
export const I18N_SKIP_MARKER = '/* i18n-skip */';

/* ───────────────────────── Runtime ───────────────────────── */

/** Shape of the dictionary file shipped with the bundle and
 *  stored in Firestore (one doc per language). */
export interface StringsDict {
  /** Bumped if we ever change the hash algorithm or schema. */
  version: number;
  /** hash → source. Source-as-value lets a future translator UI
   *  display "what should this become in {language}" without
   *  another lookup. */
  entries: Record<string, string>;
}

export const DICT_VERSION = 1;
